/**
 * Crop-box geometry for the Crop Editor (screen 1d). Pure math, unit-tested.
 *
 * MODEL — "box within a fixed image" (the simpler correct model per the build
 * spec): the photo renders at a fixed contain-fit in the canvas and the crop
 * box floats OVER it in normalized [0,1] image coordinates. Dragging moves the
 * box, corner handles resize it (aspect-locked around the opposite corner),
 * and the zoom slider scales the box about its centre — zooming IN shrinks the
 * box (a tighter crop = a more zoomed-in print). The box rect IS the stored
 * CropRect: no extra pan/scale state, no coordinate conversion on save.
 *
 * ROTATION — the manifest has no rotation field, so nothing is persisted for
 * ⟳ Rotate. Rotating simply swaps the crop box orientation (e.g. a 4×6 target
 * flips between landscape 6:4 and portrait 4:6). The bake agent's print
 * targets are orientation-following, so a portrait crop rect automatically
 * bakes onto a portrait sheet — no rotation metadata needed.
 *
 * Aspect bookkeeping: a crop box whose PIXEL shape must match the print aspect
 * has a different w/h in normalized units (normalized w = px / imageW). All
 * functions below take `a` = the box's NORMALIZED w/h ratio; use normAspect()
 * to derive it from the print's pixel aspect.
 */

import type { CropRect } from './types';
import { clamp } from './util';

export type Corner = 'nw' | 'ne' | 'sw' | 'se';

/** Slider zoom ranges: standard prints allow 4×, panoramas clamp to 2×. */
export const MAX_ZOOM = 4;
export const PANO_MAX_ZOOM = 2;

/**
 * Normalized w/h of a crop box with pixel aspect `pxAspect` (= boxPxW/boxPxH)
 * over an imgW×imgH image.
 */
export function normAspect(pxAspect: number, imgW: number, imgH: number): number {
  return (pxAspect * imgH) / imgW;
}

/** The largest centered rect with normalized aspect `a` inside the unit square. */
export function maxAspectRect(a: number): CropRect {
  const w = a >= 1 ? 1 : a;
  const h = a >= 1 ? 1 / a : 1;
  return { x: (1 - w) / 2, y: (1 - h) / 2, w, h };
}

/** Shift a rect back inside the unit square (size preserved). */
export function clampPan(r: CropRect): CropRect {
  return { ...r, x: clamp(r.x, 0, 1 - r.w), y: clamp(r.y, 0, 1 - r.h) };
}

/** Translate by (dx, dy), clamped to bounds. */
export function moveRect(r: CropRect, dx: number, dy: number): CropRect {
  return clampPan({ ...r, x: r.x + dx, y: r.y + dy });
}

/**
 * Click-to-position (Clarity dead-click fix): re-centre the box toward a
 * clicked point (normalized image coordinates), clamped inside the image.
 * Size never changes — a pure slide, like clicking in most photo apps.
 */
export function centerRectAt(r: CropRect, cx: number, cy: number): CropRect {
  return clampPan({ ...r, x: cx - r.w / 2, y: cy - r.h / 2 });
}

/* ============================================================
   Drag-to-crop on the Review-crops cards (round 8)
   ============================================================ */

/** Axes a crop window has room to slide along inside the unit square. */
export type SlackAxis = 'x' | 'y' | 'both' | 'none';

/**
 * Which axes a crop window can move on. A CENTERED AUTO-CROP always spans the
 * full image on one axis (w or h === 1 by construction), so it slides along
 * exactly one axis; a zoomed custom crop can have slack on both. `eps`
 * absorbs float noise so a hair-under-1 dimension doesn't count as slack.
 */
export function dragAxisFor(rect: CropRect, eps = 1e-4): SlackAxis {
  const x = 1 - rect.w > eps;
  const y = 1 - rect.h > eps;
  return x && y ? 'both' : x ? 'x' : y ? 'y' : 'none';
}

/**
 * Review-card drag: shift the KEPT region by a pointer delta. (dxPx, dyPx) is
 * the pointer movement in display pixels over an image rendered dispW×dispH px;
 * the window follows the pointer in normalized [0,1] image coordinates, locked
 * to its slack axis (auto-crops move on one axis only; custom crops on any axis
 * with room) and clamped inside the image. Size never changes — a pure slide,
 * so the crop's DPI is untouched.
 */
export function dragCropRect(
  start: CropRect,
  dxPx: number,
  dyPx: number,
  dispW: number,
  dispH: number,
): CropRect {
  const axis = dragAxisFor(start);
  if (axis === 'none' || dispW <= 0 || dispH <= 0) return start;
  const dx = axis === 'y' ? 0 : dxPx / dispW;
  const dy = axis === 'x' ? 0 : dyPx / dispH;
  return moveRect(start, dx, dy);
}

/**
 * Resize about the rect centre to width `w` (aspect `a` preserved), clamped
 * to [minW .. max fit] and panned back into bounds.
 */
export function rectWithWidth(r: CropRect, a: number, w: number, minW: number): CropRect {
  const max = maxAspectRect(a);
  const cw = clamp(w, Math.min(minW, max.w), max.w);
  const ch = cw / a;
  const cx = r.x + r.w / 2;
  const cy = r.y + r.h / 2;
  return clampPan({ x: cx - cw / 2, y: cy - ch / 2, w: cw, h: ch });
}

/** Zoom implied by a rect: 1 = full fit, larger = tighter crop. */
export function zoomOf(r: CropRect, a: number): number {
  return maxAspectRect(a).w / r.w;
}

/** Rect for a slider zoom value, scaling about the current centre. */
export function rectForZoom(r: CropRect, a: number, zoom: number, maxZoom: number): CropRect {
  const max = maxAspectRect(a);
  const z = clamp(zoom, 1, maxZoom);
  return rectWithWidth(r, a, max.w / z, max.w / maxZoom);
}

/**
 * Corner-handle resize: the OPPOSITE corner stays anchored, aspect `a` is
 * preserved, and the box follows the dominant axis of the drag. (dx, dy) is
 * the pointer delta in normalized units; minW is the smallest allowed width.
 */
export function resizeFromCorner(
  r: CropRect,
  corner: Corner,
  dx: number,
  dy: number,
  a: number,
  minW: number,
): CropRect {
  const west = corner === 'nw' || corner === 'sw';
  const north = corner === 'nw' || corner === 'ne';
  // Anchor = opposite corner (fixed); moving corner starts at (cx0, cy0).
  const ax = west ? r.x + r.w : r.x;
  const ay = north ? r.y + r.h : r.y;
  const cx = (west ? r.x : r.x + r.w) + dx;
  const cy = (north ? r.y : r.y + r.h) + dy;

  // Candidate width from each axis; follow whichever the drag extends more.
  const wFromX = Math.abs(cx - ax);
  const wFromY = Math.abs(cy - ay) * a;
  let w = Math.max(wFromX, wFromY);

  // Room available from the anchor towards the moving corner.
  const availW = west ? ax : 1 - ax;
  const availH = north ? ay : 1 - ay;
  w = clamp(w, Math.min(minW, Math.min(availW, availH * a)), Math.min(availW, availH * a));
  const h = w / a;

  return {
    x: west ? ax - w : ax,
    y: north ? ay - h : ay,
    w,
    h,
  };
}
